/**
 * Presentation metadata for the census's three vocabularies: principal KIND,
 * census CATEGORY, and derived STATE.
 *
 * One source of truth so the chips, the table and any future mix bar cannot
 * drift. Sentence case throughout; "AI", "API" and "SCIM" are allowlisted
 * acronyms (frontend/scripts/check-ui-conventions.mjs).
 *
 * Kind and category are deliberately SEPARATE vocabularies rendered by separate
 * controls. They answer different questions — "what is this in the identity
 * model" versus "what is this for someone counting the estate" — and the backend
 * keeps them apart for reasons that are load-bearing there (kind is immutable and
 * constraint-bearing; category is a correctable classification). Collapsing them
 * into one filter here would quietly re-assert the conflation.
 */

import {
  Bot,
  Cloud,
  FileSearch,
  KeySquare,
  RefreshCcw,
  Server,
  Shield,
  ShieldQuestion,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { CensusCategory, CensusKind, CensusSource, CensusState } from "./schemas";

interface Meta {
  label: string;
  /**
   * Mid-sentence noun form. NOT `label.toLowerCase()` — that mangles the
   * allowlisted acronyms ("API key" → "api key").
   */
  lowerLabel: string;
  icon: LucideIcon;
  description: string;
}

export const CENSUS_KIND_META: Record<CensusKind, Meta> = {
  agent: {
    label: "AI agent",
    lowerLabel: "AI agent",
    icon: Bot,
    description: "An AI agent acting on a person's behalf under delegated, bounded authority.",
  },
  workload_identity: {
    label: "Workload identity",
    lowerLabel: "workload identity",
    icon: Server,
    description: "A non-human workload authenticating with a federated workload identity.",
  },
  api_key: {
    label: "API-key integration",
    lowerLabel: "API-key integration",
    icon: KeySquare,
    description: "A read-only, key-based integration governed under the same ceiling.",
  },
  automation_account: {
    label: "Automation account",
    lowerLabel: "automation account",
    icon: Workflow,
    description:
      "A non-interactive admin identity that authenticates with an API key rather than a passkey.",
  },
  directory_connector: {
    label: "Directory connector",
    lowerLabel: "directory connector",
    icon: RefreshCcw,
    description:
      "An external directory or HR system that authenticates to provision people into this tenant.",
  },
  auditor_token: {
    label: "Auditor access",
    lowerLabel: "auditor access",
    icon: FileSearch,
    description: "A time-boxed token issued to an external auditor for read-only review.",
  },
  enforcement_point: {
    label: "Edge deployment",
    lowerLabel: "Edge deployment",
    icon: Shield,
    description:
      "An Edge deployment this tenant operates: the machine that decides governed calls in the tenant's name, proving itself to the control plane on every call.",
  },
  unrecognized: {
    // Reached only when the server sends a kind this build has never seen —
    // a widened chk_agents_kind against an older console. The row still
    // renders, and says plainly that the label is the thing missing, not the
    // identity.
    label: "Unrecognized kind",
    lowerLabel: "unrecognized kind",
    icon: ShieldQuestion,
    description:
      "This console does not have a label for this kind yet. The identity is real and counted; only its description is missing.",
  },
};

export const CENSUS_CATEGORY_META: Record<CensusCategory, Meta> = {
  ai_agent: {
    label: "AI agents",
    lowerLabel: "AI agents",
    icon: Bot,
    description: "Agents acting under delegated authority.",
  },
  cloud_workload: {
    label: "Cloud workloads",
    lowerLabel: "cloud workloads",
    icon: Cloud,
    description:
      "Workloads holding a cloud provider's identity — Kubernetes projected tokens, instances, functions.",
  },
  pipeline: {
    label: "Pipelines",
    lowerLabel: "pipelines",
    icon: Workflow,
    description: "Per-run build and deploy job identities, issued fresh for each run.",
  },
  service_account: {
    label: "Service accounts",
    lowerLabel: "service accounts",
    icon: Server,
    description: "Long-lived named services, including automation accounts.",
  },
  api_key: {
    // NOT admin_api_keys, which is correctly a NotAPrincipal exemption on the
    // server (the credential OF an admin_users row, whose holder is already
    // counted as an automation account).
    //
    // This segment is agents.kind = 'api_key' — a bootstrap/setup integration
    // that is its own row in the agent registry and its own principal. The two
    // share a word and nothing else, which is worth stating because reading
    // "api_key" here as the exempted table makes this segment look like
    // double-counting when it is not.
    label: "API keys",
    lowerLabel: "API keys",
    icon: KeySquare,
    description: "Key-based integrations registered as their own identity.",
  },
  directory_connector: {
    label: "Directory connectors",
    lowerLabel: "directory connectors",
    icon: RefreshCcw,
    description: "Systems provisioning people into this tenant.",
  },
  auditor: {
    label: "Auditor access",
    lowerLabel: "auditor access",
    icon: FileSearch,
    description: "Time-boxed external review tokens.",
  },
  enforcement_point: {
    label: "Edge deployments",
    lowerLabel: "Edge deployments",
    icon: Shield,
    description: "The Edge deployments this tenant operates.",
  },
  unclassified: {
    // A REAL segment, never folded into another and never hidden. A workload
    // identity lands here when the issuer it is bound to carries no category —
    // and it always has a fix behind it: classify the issuer, and every workload
    // federated from it moves at once.
    label: "Unclassified",
    lowerLabel: "unclassified",
    icon: ShieldQuestion,
    description:
      "Workloads whose trusted issuer has not been classified yet. Classifying the issuer moves every workload behind it.",
  },
};

/**
 * State tone maps onto the semantic tokens, which flip for dark mode. Never raw
 * palette colours.
 *
 * "expired" and "revoked" are deliberately NOT both danger: revoked is a
 * deliberate act and reads as neutral-terminal, while expired is the one an
 * operator may not have noticed and may still be depended on somewhere.
 */
export const CENSUS_STATE_META: Record<
  CensusState,
  { label: string; tone: "primary" | "success" | "warning" | "danger" | "muted"; description: string }
> = {
  active: {
    // PRIMARY, not success, and the mock is the reason: nhi-inventory.html
    // renders `.status.active` in the primary tint, while green is the PROOF
    // hue on the same screens (`--grade-proof`). Spending green on "this is
    // switched on" makes a row holding a bearer credential look like a row
    // holding cryptographic proof, which is the one distinction the NHI surfaces
    // exist to draw.
    label: "Active",
    tone: "primary",
    description: "Can authenticate right now.",
  },
  pending: {
    label: "Pending",
    tone: "muted",
    description: "Exists, but not yet usable.",
  },
  suspended: {
    label: "Suspended",
    tone: "warning",
    description: "Deliberately switched off. Reversible.",
  },
  untrusted_issuer: {
    // Warning, not danger, and deliberately: disabling an issuer is usually
    // something an operator MEANT to do, and this state is that action landing
    // correctly. Danger would make the intended case the loudest row on the page.
    label: "Untrusted issuer",
    tone: "warning",
    description:
      "The trusted issuer this identity is bound to is switched off or no longer exists, so it cannot authenticate. The fix is on the issuer, and it moves every identity behind it.",
  },
  expired: {
    label: "Expired",
    tone: "warning",
    description: "Past its expiry. It can no longer authenticate, whatever still depends on it.",
  },
  revoked: {
    label: "Revoked",
    tone: "muted",
    description: "Withdrawn for good.",
  },
  unrecognized: {
    // An unknown state is NOT rendered as active, for the same reason StateFor
    // refuses to on the server: a word we do not understand cannot count as
    // "can act right now".
    label: "Unrecognized state",
    tone: "warning",
    description:
      "This console does not have a label for this state yet, so it is not counted as able to act.",
  },
};

/**
 * Where a row is actually managed. The census reads; every action happens on the
 * row's own surface, so each source names where that is.
 */
export const CENSUS_SOURCE_META: Record<CensusSource, { label: string }> = {
  // "Agent registry", NOT "Non-human identities". The label names WHERE a row is
  // managed, and it is rendered on the Non-human identities page itself — so the
  // old value produced "12 counted in Non-human identities" on the page of that
  // name, which reads as a fault rather than a location.
  agents: { label: "Agent registry" },
  automation_accounts: { label: "Automation accounts" },
  scim_tokens: { label: "Directory: SCIM tokens" },
  directories: { label: "Directory: AD inbound" },
  auditor_tokens: { label: "Compliance" },
  edges: { label: "Edge deployments" },
  unrecognized: { label: "Unknown surface" },
};
